import { useCallback } from 'react';
import { DealStatusConfig, FetchDealsData, InvestorDealData } from '../../../../../../../store/investor/reducer';
import { DEAL_VIEW_TYPES } from '../../../../../../../store/investor/constants';
import { getStatusDropdownOption as getStatusDropdownOptionUtil } from '../../../../../../../utils/commonUtil';
import { StatusDropdownOption } from '../../../../../../../common/_custom/StatusDropdown/StatusDropdown';
import { sortByDateDescending } from '../../../../../../../utils/dateUtils';
import CardList from '../../../../../components/CompanyCard/CardList';
import CompanyCard, { CardComment } from '../../../../../components/CompanyCard/CompanyCard';
import { fallbackDealStatusOption } from '../../DealsList';

// For a mandate, comments live on each lender (deal.investorDeals[n].comment), not on the
// mandate itself, so deal.comment alone is usually empty — surface the most recent comment
// across the mandate and all its lenders.
const getLatestMandateComment = (deal: FetchDealsData): CardComment => {
    const candidates = [deal.comment, ...(deal.investorDeals || []).map((investorDeal) => investorDeal.comment)].filter(
        (comment): comment is NonNullable<typeof comment> => !!comment?.text,
    );
    return sortByDateDescending(candidates, 'activityDate')[0] ?? null;
};

type DealCardListProps = {
    deals: FetchDealsData[];
    isLoading: boolean;
    hasNextPage: boolean;
    dealStatusConfig: DealStatusConfig | null;
    selectedCategory: string;
    currentPageNumber?: number;
    onLoadMore: () => void;
    onDealClick?: (deal: FetchDealsData | InvestorDealData) => void;
    emptyComponent?: React.ReactNode;
};

const DealCardList = ({
    deals,
    isLoading,
    hasNextPage,
    dealStatusConfig,
    selectedCategory,
    currentPageNumber = 1,
    onLoadMore,
    onDealClick,
    emptyComponent,
}: DealCardListProps) => {
    const isMandateView = selectedCategory === DEAL_VIEW_TYPES.MANDATE_VIEW;

    const getStatusOption = useCallback(
        (status?: string): StatusDropdownOption | undefined => {
            if (!status) return undefined;
            return getStatusDropdownOptionUtil(
                status,
                dealStatusConfig,
                isMandateView ? '' : selectedCategory,
                undefined,
                fallbackDealStatusOption,
            );
        },
        [dealStatusConfig, isMandateView, selectedCategory],
    );

    return (
        <CardList
            items={deals}
            loading={isLoading}
            getId={(deal) => deal.dealId}
            emptyState={emptyComponent}
            hasNextPage={hasNextPage}
            onLoadMore={onLoadMore}
            replaceAll={currentPageNumber <= 1}
            resetKey={selectedCategory}
            dataAttrs={(deal) => ({ 'data-org-id': deal.dealBeneficiaryId, 'data-org-name': deal.borrowerName })}
            renderCard={(deal) => {
                const statusOption = getStatusOption(deal.dealStatus);
                return (
                    <CompanyCard
                        name={deal.borrowerName || deal.investorDeals?.[0]?.borrowerName || (deal as any).orgName || ''}
                        orgId={deal.dealBeneficiaryId}
                        websiteUrl={deal.websiteUrl}
                        comment={isMandateView ? getLatestMandateComment(deal) : deal.comment}
                        chips={statusOption ? [statusOption] : []}
                        badges={deal.amount ? [deal.amount] : []}
                        productTypes={isMandateView ? [] : deal.loanTypes || []}
                        onCardClick={onDealClick ? () => onDealClick(deal) : undefined}
                        mandate={isMandateView ? { investorDeals: deal.investorDeals || [], getStatusOption } : undefined}
                    />
                );
            }}
        />
    );
};

export default DealCardList;
